/**
 * Generates PWA PNG icons at 192x192 and 512x512 using Canvas API.
 * Stores them in Cache Storage so the Service Worker can serve them
 * as /icons/icon-192.png and /icons/icon-512.png.
 */

function drawIconCanvas(size: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return reject(new Error('Canvas 2D context unavailable'));

    const r = size * 0.167;
    const cx = size / 2;

    // Rounded rect background
    const grad = ctx.createLinearGradient(0, 0, size, size);
    grad.addColorStop(0, '#0f766e');
    grad.addColorStop(1, '#0d9488');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(size - r, 0);
    ctx.quadraticCurveTo(size, 0, size, r);
    ctx.lineTo(size, size - r);
    ctx.quadraticCurveTo(size, size, size - r, size);
    ctx.lineTo(r, size);
    ctx.quadraticCurveTo(0, size, 0, size - r);
    ctx.lineTo(0, r);
    ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.closePath();
    ctx.fill();

    // Water drop outer (white)
    const dropHalfW = size * 0.198;
    const dropCenterY = size * 0.578;
    const dropTopY = size * 0.198;

    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.beginPath();
    ctx.moveTo(cx, dropTopY);
    ctx.bezierCurveTo(
      cx + dropHalfW * 0.1, size * 0.36,
      cx + dropHalfW, size * 0.48,
      cx + dropHalfW, dropCenterY
    );
    ctx.arc(cx, dropCenterY, dropHalfW, 0, Math.PI, false);
    ctx.bezierCurveTo(
      cx - dropHalfW, size * 0.48,
      cx - dropHalfW * 0.1, size * 0.36,
      cx, dropTopY
    );
    ctx.closePath();
    ctx.fill();

    // IR text
    const fontSize = Math.round(size * 0.148);
    ctx.fillStyle = '#0d9488';
    ctx.font = `800 ${fontSize}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('IR', cx, dropCenterY + size * 0.01);

    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('toBlob returned null'));
    }, 'image/png');
  });
}

export async function generateAndCachePwaIcons(): Promise<void> {
  if (!('caches' in window)) return;

  const CACHE_NAME = 'pwa-icons-v1';
  const sizes = [192, 512];

  try {
    const cache = await caches.open(CACHE_NAME);

    for (const size of sizes) {
      const url = `/icons/icon-${size}.png`;

      // Skip if already cached
      const existing = await cache.match(url);
      if (existing) continue;

      const blob = await drawIconCanvas(size);
      const response = new Response(blob, {
        headers: {
          'Content-Type': 'image/png',
          'Cache-Control': 'public, max-age=31536000',
        },
      });
      await cache.put(url, response);
    }
  } catch (err) {
    console.warn('[PWA Icons] Failed to generate/cache icons:', err);
  }
}
